import React, {
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import StickyBox from 'react-sticky-box';
import { useQueryClient } from 'react-query';
import {
  Grid,
  Typography,
  Box,
  Hidden,
  Paper,
  FormGroup,
  FormControlLabel,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Tooltip,
  TextField,
  MenuItem,
  Switch,
  Alert,
  AlertTitle,
  Badge,
} from '@mui/material';
import {
  FilterAlt,
  Fullscreen,
  Publish,
  ViewCompact,
  Article,
  Compare,
  Edit,
  QuestionAnswerOutlined,
  FilePresent,
  ViewComfy,
} from '@mui/icons-material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import {
  usePartialPublish,
  useSectionsToPublish,
  useArticlesToPublish,
  setPartialPublish,
} from 'store/DocumentPublish';

import {
  setShowFilesOverview,
  toggleShowDocumentSidebar,
  toggleShowFilters,
  setShowRightContainer,
} from 'store/DocumentTemplate';
import {
  setDraftPreviewMode,
  setMode as setDocumentMode,
} from 'store/DocumentMode';
import { find } from 'lodash';
import { setRenvoiMode } from 'store/DocumentSearch';
import { primaryLight } from 'shared/styles/muiTheme';
import { setHistoryData } from 'store/Document';
import { useBoundStore } from 'store';

import {
  changesSaved,
  genericError,
  setLayoutTempState,
  setShowFAQ,
} from 'store/Layout';
import { useDialogContext } from 'store/Dialog';
import { Dialog } from 'store/types';
import { useBlocksToPublish } from 'store/DocumentPublish';
import { format } from 'date-fns';
import { markReviewModeForRefresh, resetReviewMode, setReviewModeData } from 'store/DocumentReview';
import { setIsPrintMode, setIsPrintPreview } from 'store/DocumentPrint';
import { cacheKeys } from 'config';
import {
  LocalizationContext,
  UserContext,
} from 'contexts';
import { DocumentMode } from 'types/Document';

import { RenvoiMode } from 'clients/documents/documentsClient.types';
import { documentsClient } from 'clients/documents/documentsClient';
import { SectionMenu } from 'modules/Document/SectionMenu/SectionMenu';
import { MobileDocumentNav } from 'modules/Document/SectionMenu/MobileDocumentNav';
import { SearchResultsNav } from 'modules/Document/SearchResultsNav/SearchResultsNav';
import { SearchInput } from 'modules/Document/SearchInput/SearchInput';
import { RenvoiModeDropDown } from 'modules/Document/RenvoiModeDropdown/RenvoiModeDropDown';
import { ImportantOnlyDropDown } from 'modules/Document/ImportantOnlyDropDown/ImportantOnlyDropDown';
import FAQAndNotesContainer from 'modules/Document/FAQAndNotesContainer/FAQAndNotesContainer';
import { FilesOverviewContainer } from 'modules/Document/FilesOverviewContainer';
import { DocumentRightContainer } from 'modules/Document/DocumentRightContainer/DocumentRightContainer';
import { useLazyHashInitScrollEffect } from 'shared/hooks/useLazyHashInitScrollEffect';

import { useQueryState } from 'shared/hooks/useQueryState';
import {
  useDocument,
  useDocumentComparisonHistory,
  usePrefetchDocument,
  useDocumentVersion,
} from 'shared/hooks/useDocument';
import { useOnLeavePage } from 'shared/hooks/useOnLeavePage';
import { useNotes } from 'shared/hooks/useNotes';
import { BodyLoading } from 'shared/components/layout/BodyLoading/BodyLoading';
import { VersionComparisonPanel } from './components/VersionComparisonPanel';
import { DocumentShareButton } from './components/DocumentShareButton';
import {
  RenderDocumentContainer,
  RenderedDocument,
} from './components/RenderedDocument';
import { useReviewModeRefresh } from './components/useReviewModeRefresh';

const prefetch = false;
const showSectionList = false;

type DocumentProps = {
	mode?: DocumentMode;
	isPrintMode?: boolean;
	isPrintPreview?: boolean;
};

export const DocumentWithContexts: FC<DocumentProps> = ({
  mode: documentModeProp,
  isPrintMode,
  isPrintPreview,
}) => {
  const queryClient = useQueryClient();
  const {
    writeMode,
    reviewMode,
    readMode,
    documentMode,
    isMobile,
    isLightMode,
    showFAQ,
    layoutTempState,
    reviewModeData,
  } = useBoundStore((state) => ({
    writeMode: state.writeMode,
    reviewMode: state.reviewMode,
    readMode: state.readMode,
    documentMode: state.mode,
    isMobile: state.isMobile,
    isLightMode: state.isLightMode,
    showFAQ: state.showFAQ,
    layoutTempState: state.layoutTempState,
    reviewModeData: state.reviewModeData,
  }));
  const { language } = useContext(LocalizationContext);

  const { openDialog } = useDialogContext();
  const { userPermissions } = useContext(UserContext);
  const { dictionary } = useContext(LocalizationContext);
  const partialPublish = usePartialPublish();
  const sectionsToPublish = useSectionsToPublish();
  const articlesToPublish = useArticlesToPublish();
  const blocksToPublish = useBlocksToPublish();
  const [slug, setSlug] = useQueryState('slug');
  const [importantOnly] = useQueryState('important-only');
  const [hideNav] = useQueryState('hide-nav');
  const [sectionId, setSectionId] = useQueryState('section-id');
  const showFilesOverview = useBoundStore((state) => state.showFilesOverview);
  // eslint-disable-next-line
	const [sourceDocumentTitle, setSourceDocumentTitle] = useState<string>('');
  // eslint-disable-next-line
	const [targetDocumentTitle, setTargetDocumentTitle] = useState<string>('');

  const [showComparisonPanel, setShowComparisonPanel] = useState(false);
  const [comparisonChangesCount, setComparisonChangesCount] = useState(0);
  // eslint-disable-next-line
	const [isPublishing, setIsPublishing] = useState(false);
  const [versionHistoryChecked, setVersionHistoryChecked] = useState(false);

  const showDocumentSidebar = useBoundStore(
    (state) => state.showDocumentSidebar,
  );

  const showFilters = useBoundStore((state) => state.showFilters);

  const showRightContainer = useBoundStore((state) => state.showRightContainer);

  const { document, draftDocument, status: docStatus, error: docError, isEnabled: docEnabled } = useDocument(
    { slug, importantOnly: !!importantOnly },
    { enabled: true, loadDraft: !reviewMode },
  );

  useEffect(() => {
    setIsPrintMode(!!isPrintMode);
    setIsPrintPreview(!!isPrintPreview);

    return () => {
      setIsPrintMode(false);
      setIsPrintPreview(false);
    };
  }, [isPrintMode, isPrintPreview]);

  useEffect(() => {
    const checkVersionHistory = async () => {
      if (slug && !versionHistoryChecked) {
        try {
          const response = await documentsClient.getVersionHistory(slug);
          const { current_version } = response.data;

          if (current_version && slug !== current_version) {
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.set('slug', current_version);
            const newUrl = `${window.location.pathname}?${currentParams.toString()}${window.location.hash}`;
            window.history.replaceState(null, '', newUrl);
            setSlug(current_version);
          }
        } catch (error) {
          console.error('Failed to fetch version history:', error);
        } finally {
          setVersionHistoryChecked(true);
        }
      }
    };

    checkVersionHistory();
  }, [slug, versionHistoryChecked, setSlug]);

  const sourceSlug = reviewModeData?.sourceSlug ?? slug;
  const targetSlug = reviewModeData?.targetSlug ?? slug;
  const sourceIsDraft = reviewModeData?.sourceIsDraft ?? false;
  const targetIsDraft = reviewModeData?.targetIsDraft ?? true;

  const handleDocumentTitleChange = useCallback((sourceTitle: string, targetTitle: string) => {
    setSourceDocumentTitle(sourceTitle);
    setTargetDocumentTitle(targetTitle);
  }, []);

  const {
    document: sourceDocument,
    status: sourceStatus,
    isLoading: sourceIsLoading,
    isFetching: sourceIsFetching,
    isEnabled: sourceEnabled,
  } = useDocumentVersion({
    slug: sourceSlug,
    isDraft: sourceIsDraft,
    importantOnly: !!importantOnly,
  }, {
    enabled: reviewMode && !!sourceSlug,
  });

  const {
    document: targetDocument,
    status: targetStatus,
    isLoading: targetIsLoading,
    isFetching: targetIsFetching,
    isEnabled: targetEnabled,
  } = useDocumentVersion({
    slug: targetSlug,
    isDraft: targetIsDraft,
    importantOnly: !!importantOnly,
  }, {
    enabled: reviewMode && !!targetSlug,
  });

  useNotes();

  const isSourceLoading = Boolean(
    reviewMode &&
			sourceSlug &&
			reviewModeData?.isInitialized &&
			(!sourceEnabled || sourceIsLoading || sourceIsFetching || sourceStatus === 'loading'),
  );

  const isTargetLoading = Boolean(
    reviewMode &&
			targetSlug &&
			reviewModeData?.isInitialized &&
			(!targetEnabled || targetIsLoading || targetIsFetching || targetStatus === 'loading'),
  );

  const renvoiMode = useBoundStore((state) => state.renvoiMode);

  const toggleFAQ = useCallback(() => {
    const currentShowFAQ = useBoundStore.getState().showFAQ;
    const newShowFAQ = !currentShowFAQ;

    if (!currentShowFAQ) {
      setLayoutTempState({
        ...(layoutTempState || {}),
        selectedFAQ: [],
        FAQItemType: undefined,
        FAQItem: undefined,
      });
      setShowFilesOverview(false);
    }

    setShowRightContainer(newShowFAQ);
    setShowFAQ(newShowFAQ);
  }, [layoutTempState]);

  const publishDocument = useCallback(() => {
    if (slug) {
      openDialog(Dialog.PublishDocument, {
        slug,
        currentPublicationDate: document?.published_at ? format(new Date(document.published_at), 'dd-MM-yyyy') : '06-02-2025',
        onPublishSuccess: async () => {
          markReviewModeForRefresh();

          await Promise.all([
            queryClient.invalidateQueries(cacheKeys.history.getHistory),
            queryClient.invalidateQueries(cacheKeys.documents.getDocument),
            queryClient.invalidateQueries(cacheKeys.documents.getDocuments),
            queryClient.invalidateQueries(cacheKeys.documents.getDraftDocument),
            queryClient.invalidateQueries(
              cacheKeys.documents.getSearchDropdownData,
            ),
          ]);
          changesSaved(dictionary);

          if (reviewMode) {
            setTimeout(() => {
              refreshReviewModeData();
            }, 100);
          }
        },
        publishData: partialPublish
          ? {
            sections: sectionsToPublish,
            articles: articlesToPublish,
            blocks: blocksToPublish,
            recursive_block_publish: true,
            recursive_article_publish: false,
            partial_publish: true,
            publish_cla_metadata: false,
          }
          : {
            recursive_block_publish: true,
            recursive_article_publish: true,
            partial_publish: false,
            publish_cla_metadata: false,
          },
      });
    }
    // eslint-disable-next-line
	}, [
    dictionary,
    partialPublish,
    sectionsToPublish,
    articlesToPublish,
    blocksToPublish,
    slug,
    queryClient,
    document?.published_at,
  ]);

  const draftSections = useMemo(() => {
    if (
      !reviewMode &&
				sectionId !== undefined &&
				draftDocument?.section_set &&
				draftDocument?.section_set?.[sectionId]
    ) {
      return [draftDocument?.section_set?.[sectionId]];
    }
    return draftDocument?.section_set;
  }, [reviewMode, sectionId, draftDocument]);

  const sections = useMemo(() => {
    const section = find(document?.section_set, {
      id: parseInt(sectionId as string),
    });
    if (
      !reviewMode &&
				sectionId !== undefined &&
				document?.section_set &&
				section
    ) {
      setSectionId(section.id);
      return [section];
    }
    return document?.section_set;
  }, [reviewMode, sectionId, setSectionId, document]);

  const sourceSections = useMemo(() => {
    if (!reviewMode || !sourceDocument) return null;
    const section = find(sourceDocument?.section_set, {
      id: parseInt(sectionId as string),
    });
    if (sectionId !== undefined && sourceDocument?.section_set && section) {
      return [section];
    }
    return sourceDocument?.section_set;
  }, [reviewMode, sectionId, sourceDocument]);

  const targetSections = useMemo(() => {
    if (!reviewMode || !targetDocument) return null;
    const section = find(targetDocument?.section_set, {
      id: parseInt(sectionId as string),
    });
    if (sectionId !== undefined && targetDocument?.section_set && section) {
      return [section];
    }
    return targetDocument?.section_set;
  }, [reviewMode, sectionId, targetDocument]);

  usePrefetchDocument(
    { slug, importantOnly: !importantOnly },
    { enabled: prefetch && docStatus === 'success' },
  );

  useLazyHashInitScrollEffect();

  useEffect(() => {
    const newDocumentMode = documentModeProp !== undefined
      ? (documentModeProp as DocumentMode)
      : documentMode;

    setDocumentMode(newDocumentMode);

    if (newDocumentMode !== DocumentMode.Review && reviewMode) {
      resetReviewMode();
    }

    if (newDocumentMode === DocumentMode.Review && !reviewMode) {
      const initialSourceSlug = slug || '';
      const initialTargetSlug = slug || '';

      setReviewModeData({
        sourceSlug: initialSourceSlug,
        targetSlug: initialTargetSlug,
        sourceIsDraft: false,
        targetIsDraft: true,
        isInitialized: true,
        needsRefresh: false,
      });
    }

    if (isPrintMode || isPrintPreview) {
      setRenvoiMode(RenvoiMode.Normal);
    } else {
      setRenvoiMode(
        (newDocumentMode === DocumentMode.Read || newDocumentMode === DocumentMode.Review)
          ? RenvoiMode.Normal
          : RenvoiMode.Strikethrough,
      );
    }
  }, [documentModeProp, documentMode, isPrintPreview, isPrintMode, slug, reviewMode]);

  useEffect(() => {
    if (reviewMode && slug && reviewModeData?.isInitialized) {
      if (reviewModeData.sourceSlug !== slug || reviewModeData.targetSlug !== slug) {
        setReviewModeData({
          sourceSlug: slug,
          targetSlug: slug,
          sourceIsDraft: false,
          targetIsDraft: true,
          isInitialized: true,
          needsRefresh: false,
        });
      }
    }

    // eslint-disable-next-line
	}, [slug, reviewMode]);

  useEffect(() => {
    setDraftPreviewMode(!!isPrintMode);
  }, [isPrintMode]);

  const { historyData, status: historyStatus } =
			useDocumentComparisonHistory(slug, { enabled: false });

  const { refreshReviewModeData } = useReviewModeRefresh(slug);

  useEffect(() => {
    if (reviewMode) {
      markReviewModeForRefresh();
      refreshReviewModeData();
    }
    // eslint-disable-next-line
	}, [reviewMode, language]);

  useEffect(() => {
    if (historyStatus === 'success' && historyData) {
      setHistoryData(historyData);
      console.log(historyData);
    }
  }, [historyData, historyStatus]);

  const onPrintStart = useCallback(() => {
    openDialog(Dialog.PrintDocument, { showVersionFilters: true });
  }, [openDialog]);

  const handleVersionsSelected = useCallback((
    sourceSlug: string,
    targetSlug: string,
    sourceIsDraft: boolean,
    targetIsDraft: boolean,
  ) => {


    setReviewModeData({
      sourceSlug,
      targetSlug,
      sourceIsDraft,
      targetIsDraft,
      isInitialized: true,
      needsRefresh: false,
    });

  }, []);

  const handleComparisonDataChange = useCallback((changesCount: number) => {
    setComparisonChangesCount(changesCount);
  }, []);

  const handleComparisonPanelClose = useCallback(() => {
    setShowComparisonPanel(false);
  }, []);

  useEffect(() => {
    if (reviewMode) {
      setShowComparisonPanel(true);
    } else {
      setShowComparisonPanel(false);
    }
    // eslint-disable-next-line
	}, [reviewMode]);

  if (docEnabled && docStatus === 'error') {
    return (
      <Alert
        severity="error"
        sx={{
          m: 4,
        }}
      >
        <AlertTitle>{dictionary.publicDocument.error.title}</AlertTitle>
        {dictionary.publicDocument.error.description}
      </Alert>
    );
  }

  if (docError) {
    genericError(dictionary);
    return null;
  }

  if (isPublishing) return <BodyLoading/>;
  if (!docEnabled || docStatus === 'loading') return <BodyLoading/>;

  return (
    <Box bgcolor={isLightMode ? '#ececec' : '#111'}>
      {reviewMode && (
        <VersionComparisonPanel
          show={showComparisonPanel}
          onClose={handleComparisonPanelClose}
          currentClaSlug={slug || ''}
          onVersionsSelected={handleVersionsSelected}
          onDocumentTitleChange={handleDocumentTitleChange}
          onComparisonDataChange={handleComparisonDataChange}
        />
      )}

      {!isPrintMode && (
        <StickyBox
          key={`sticky-box-${isMobile}`}
          offsetTop={hideNav ? 0 : isMobile ? 56 : 100}
          style={{
            zIndex: 100,
            marginLeft: reviewMode && showComparisonPanel ? '0' : '0',
            transition: 'margin-left 0.3s ease-in-out',
          }}
          className="no-print"
        >
          <Paper
            sx={{
              p: 2,
              background: primaryLight,
            }}
          >
            <Grid
              container
              alignItems="center"
              wrap={!isMobile ? 'nowrap' : undefined}
              spacing={2}
            >
              <Fragment>
                {!reviewMode && (
                  <Fragment>
                    <Grid item>
                      <IconButton onClick={toggleShowDocumentSidebar}>
                        {showDocumentSidebar ? <Fullscreen/> : <ViewCompact/>}
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <Tooltip title={dictionary.export}>
                        <IconButton onClick={onPrintStart} edge="start">
                          <CloudDownloadIcon/>
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Fragment>
                )}
                {reviewMode && (
                  <Grid item>
                    <Tooltip title="Show version comparison">
                      <IconButton
                        onClick={() => setShowComparisonPanel(!showComparisonPanel)}
                        edge="start"
                      >
                        <Badge badgeContent={comparisonChangesCount || 0} color="error">
                          <ViewComfy/>
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
                <Grid item>
                  <Tooltip title={dictionary.toggleFilters}>
                    <IconButton onClick={toggleShowFilters} edge="start">
                      <FilterAlt/>
                    </IconButton>
                  </Tooltip>
                </Grid>
                {showFilters && (
                  <Fragment>
                    {!reviewMode && (document || draftDocument) && (
                      <Grid item xs={12} sm="auto">
                        <TextField
                          label={dictionary.section}
                          size="small"
                          sx={{ width: 200 }}
                          defaultValue={sectionId}
                          onChange={(e) =>
                            setSectionId(
                              e.target.value === 'all'
                                ? null
                                : e.target.value.toString(),
                            )
                          }
                          select
                        >
                          <MenuItem value="all">{dictionary.all}</MenuItem>
                          {(writeMode
                            ? draftDocument
                            : document
                          )?.section_set?.map((section: any) => (
                            <MenuItem key={section.slug} value={section.id}>
                              {section?.stripped_title}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    {!reviewMode && (
                      <Grid item xs={12}>
                        <SearchInput document={document}/>
                      </Grid>
                    )}
                    <Grid item xs={12} sm="auto">
                      <SearchResultsNav/>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm="auto"
                      textAlign="right"
                      sx={{ ml: -2 }}
                    >
                      <ImportantOnlyDropDown/>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm="auto"
                      textAlign="right"
                      sx={{ ml: -2 }}
                    >
                      <RenvoiModeDropDown/>
                    </Grid>
                  </Fragment>
                )}
              </Fragment>

              {reviewMode && !!userPermissions?.publish_cla && (
                <Fragment>
                  <Grid item xs={12} sm="auto">
                    <Button
                      onClick={publishDocument}
                      disabled={isPublishing}
                      endIcon={<Publish style={{ fill: '#ececec' }}/>}
                    >
                      {dictionary.editableContent.publish}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm="auto" sx={{ mr: -2 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={partialPublish}
                            onChange={(e) =>
                              setPartialPublish(e.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography variant="subtitle2">
                            {partialPublish
                              ? dictionary.partial
                              : dictionary.full}
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Grid>
                </Fragment>
              )}
              <Box ml="auto"/>

              {!!userPermissions?.share_cla && (
                <Grid item>
                  <Tooltip title={dictionary.share}>
                    <DocumentShareButton slug={slug}/>
                  </Tooltip>
                </Grid>
              )}

              <Grid item>
                <ToggleButtonGroup
                  size="small"
                  value={[showFAQ, showFilesOverview]}
                  onChange={() => {
                  }}
                  exclusive
                >
                  {!!userPermissions?.view_questionanswer && !reviewMode && (
                    <ToggleButton
                      value={[true, false]}
                      selected={showFAQ && showRightContainer}
                      onMouseDown={toggleFAQ}
                    >
                      <Tooltip title={dictionary.faq.faq}>
                        <QuestionAnswerOutlined/>
                      </Tooltip>
                    </ToggleButton>
                  )}
                  {!!userPermissions?.view_file && !reviewMode && (
                    <ToggleButton
                      value={[false, true]}
                      selected={showFilesOverview && showRightContainer}
                      onMouseDown={() => {
                        if (showRightContainer && showFAQ) {
                          setShowFilesOverview(true);
                          setShowRightContainer(true);
                          setShowFAQ(false);
                        } else if (showRightContainer && showFilesOverview) {
                          setShowFilesOverview(false);
                          setShowRightContainer(false);
                        } else {
                          setShowFilesOverview(true);
                          setShowRightContainer(true);
                        }
                      }}
                    >
                      <Tooltip title={dictionary.document.files}>
                        <FilePresent/>
                      </Tooltip>
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Grid>

              {!!userPermissions?.change_cla && (
                <Grid item xs={12} sm="auto">
                  <ToggleButtonGroup
                    size="small"
                    value={documentModeProp || documentMode}
                    onChange={(e, value) => {
                      window.scrollTo({ top: 0 });
                      setDocumentMode(value || DocumentMode.Read);
                    }}
                    exclusive
                  >
                    <ToggleButton value={DocumentMode.Read}>
                      <Tooltip title={dictionary.documentStatuses.read}>
                        <Article/>
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value={DocumentMode.Write}>
                      <Tooltip title={dictionary.documentStatuses.write}>
                        <Edit/>
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value={DocumentMode.Review}>
                      <Tooltip title={dictionary.documentStatuses.review}>
                        <Compare/>
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              )}
            </Grid>
          </Paper>
        </StickyBox>
      )}
      <Box
        p={isPrintMode ? undefined : 2}
        pt={isPrintMode ? undefined : 4}
        className={`renvoi-mode-${renvoiMode} print-document-container`}
        id="document"
        sx={{
          marginLeft: reviewMode && showComparisonPanel ? '0' : '0',
          transition: 'margin-left 0.3s ease-in-out',
        }}
      >
        <Box
          sx={{
            paddingLeft: isPrintMode ? 0 : undefined,
            paddingRight: isPrintMode ? 0 : undefined,
          }}
        >
          <Grid
            container
            alignItems="top"
            wrap="nowrap"
            spacing={isPrintMode ? undefined : 1}
            display="flex"
            justifyContent="center"
          >
            {showDocumentSidebar && !reviewMode && !isPrintMode && (
              <Fragment>
                <Hidden mdDown>
                  <Grid
                    item
                    className="no-print"
                    sx={{
                      mr: 2,
                      maxWidth: '480px',
                      minWidth: '300px',
                    }}
                  >
                    <StickyBox offsetTop={190} style={{ zIndex: 50 }}>
                      <Box textAlign="center" sx={{ mb: 2 }}>
                        <Typography variant="h6">
                          {dictionary.tableOfContents}
                        </Typography>
                      </Box>
                      <Box maxHeight="calc(100vh - 280px)" overflow="auto">
                        <SectionMenu
                          sections={writeMode ? draftSections : sections}
                        />
                      </Box>
                    </StickyBox>
                  </Grid>
                </Hidden>
                <Hidden mdUp>
                  <MobileDocumentNav
                    sections={writeMode ? draftSections : sections}
                  />
                </Hidden>
              </Fragment>
            )}

            {reviewMode && (
              <RenderDocumentContainer
                reviewMode={reviewMode}
                isPrintMode={isPrintMode}
                showDocumentSidebar={showDocumentSidebar}
                showRightContainer={showRightContainer}
                id="document-source"
              >
                <RenderedDocument
                  document={sourceDocument}
                  sections={sourceSections}
                  reviewMode={reviewMode}
                  reviewModeTitle={sourceDocument ? `Source: ${sourceDocument.published_at}  - ${sourceIsDraft ? 'DRAFT' : 'PUBLISHED'}` : 'Loading...'}
                  showSectionList={showSectionList}
                  isLightMode={isLightMode}
                  isCanEditTitle={false}
                  isPrintPreview={isPrintPreview}
                  isLoading={isSourceLoading}
                />
              </RenderDocumentContainer>
            )}

            {reviewMode && (
              <RenderDocumentContainer
                reviewMode={reviewMode}
                isPrintMode={isPrintMode}
                showDocumentSidebar={showDocumentSidebar}
                showRightContainer={showRightContainer}
                id="document-target"
              >
                <RenderedDocument
                  document={targetDocument}
                  sections={targetSections}
                  reviewMode={reviewMode}
                  reviewModeTitle={targetDocument ? `Target: ${targetDocument.published_at}  (${targetIsDraft ? 'draft' : 'published'})` : 'Loading...'}
                  showSectionList={showSectionList}
                  isLightMode={isLightMode}
                  isCanEditTitle={false}
                  isPrintPreview={isPrintPreview}
                  isLoading={isTargetLoading}
                />
              </RenderDocumentContainer>
            )}

            {(readMode && document) && (
              <RenderDocumentContainer
                reviewMode={false}
                isPrintMode={isPrintMode}
                showDocumentSidebar={showDocumentSidebar}
                showRightContainer={showRightContainer}
                id="document-published"
              >
                <RenderedDocument
                  document={document}
                  sections={sections}
                  reviewMode={false}
                  reviewModeTitle={dictionary.documentStatuses.read}
                  showSectionList={showSectionList}
                  isLightMode={isLightMode}
                  isCanEditTitle={userPermissions?.rename_cla || false}
                  isPrintPreview={isPrintPreview}
                />
              </RenderDocumentContainer>
            )}

            {(writeMode && draftDocument) && (
              <RenderDocumentContainer
                reviewMode={false}
                isPrintMode={isPrintMode}
                showDocumentSidebar={showDocumentSidebar}
                showRightContainer={showRightContainer}
                id="document-draft"
              >
                <RenderedDocument
                  document={draftDocument}
                  sections={draftSections}
                  reviewMode={false}
                  reviewModeTitle={dictionary.documentStatuses.write}
                  showSectionList={showSectionList}
                  isLightMode={isLightMode}
                  isCanEditTitle={userPermissions?.rename_cla || false}
                  isPrintPreview={isPrintPreview}
                />
              </RenderDocumentContainer>
            )}

            <DocumentRightContainer
              show={showRightContainer}
              onClose={() => {
                setShowRightContainer(false);
                setShowFAQ(false);
                setShowFilesOverview(false);
              }}
            >
              {showFAQ && !reviewMode && <FAQAndNotesContainer/>}

              {showFilesOverview && !reviewMode && (
                <FilesOverviewContainer
                  document={writeMode ? draftDocument : document}
                />
              )}
            </DocumentRightContainer>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export const Document: FC<DocumentProps> = (props) => {
  return (
    <DocumentWithContexts {...props} />
  );
};

export const DocumentRouteView: FC = () => {
  const { layoutTempState } = useBoundStore((state) => ({
    layoutTempState: state.layoutTempState,
  }));

  const onLeavePage = useCallback(() => {
    setLayoutTempState({
      ...layoutTempState,
      itemsToPrint: {},
      selectedFAQ: [],
      FAQItem: undefined,
      FAQItemType: undefined,
    });
    setShowFAQ(false);
  }, [layoutTempState]);

  useOnLeavePage(onLeavePage);

  return <Document/>;
};